import { Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { routes, RouteInfo } from './routes';
import { useCookies } from "react-cookie";
import { GET_CURRENT_USER_WITH_SIDE_NAV, SIGN_OUT_MUTATION } from "./shared/graphql/auth/auth.gql";
import { useLazyQuery, useMutation} from "@apollo/client";
import './App.less';
import Dashboard from './pages/dashboard/Dashboard';
import Login from './pages/login/Login';
import Header from './shared/components/header/GeneralHeader';
import SideMenu from './shared/components/side-menu/SideMenu';
import Footer from './shared/components/footer/GeneralFooter';
import { Layout, Spin, notification } from 'antd';
import { 
    CLEAR_CURRENT_USER, 
    SET_CURRENT_USER, 
    SET_LOGIN_STATUS, 
    SET_INITIALIZED, 
    useAuth 
} from './hooks/auth/AuthContext';
import { CLEAR_NOTIFICATION, useLayout } from './hooks/layout/LayoutContext';
import { authClient, client } from './apollo-clients';
import useSpinner from './hooks/layout/useSpinner';
import messages_en from './assets/i18n/en.json';
import message_zh_CN from './assets/i18n/zh_CN.json';
import { IntlProvider } from 'react-intl';
import Cookies from 'universal-cookie';
import SignUp from './pages/sign-up/SignUp';
import { GET_CONFIGURATION_LIST } from './shared/graphql/configuration.gql';

const { Content } = Layout;

const App = () => {
  const cookiesUni = new Cookies();
  const navigate = useNavigate();
  const { authState, authDispatch } = useAuth();
  const { layoutState, layoutDispatch } = useLayout();
  const { setLoading } = useSpinner();
  const [cookies, , removeCookie] = useCookies();
  
  const [signOut] = useMutation(SIGN_OUT_MUTATION, {
    client: client, // Use authenticated client instead of authClient
    errorPolicy: "ignore",
    onError: (err: any) => signOutCleanUp()
  });

  const [getCurrentUser, { loading, data, error, called, refetch }] = useLazyQuery(GET_CURRENT_USER_WITH_SIDE_NAV, {
    client: client,
    fetchPolicy: 'network-only',
    errorPolicy: "all"
  });

  const aToken: any = process.env.REACT_APP_ACCESS_TOKEN || 'sc-at';
  const rToken: any = process.env.REACT_APP_REFRESH_TOKEN || 'sc-rt';

  const messages: any = {
    'en': messages_en,
    'zh-CN': message_zh_CN
  };

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      
      const accessToken = cookies[aToken];
      const refreshToken = cookies[rToken];
      
      
      if (accessToken) {
        authDispatch({ type: SET_LOGIN_STATUS, payload: true });
        
        try {
          // Only fetch user if we haven't been logged out during this process
          if (cookies[aToken]) {
            await getCurrentUser();
          }
        } catch (error) {
          console.error('Failed to get user profile:', error);
          // Token might be expired, cleanup
          signOutCleanUp();
        }
      } else if (refreshToken) {
        // TODO: Implement token refresh
        signOutCleanUp();
      } else {
        authDispatch({ type: SET_LOGIN_STATUS, payload: false });
      }
      
      authDispatch({ type: SET_INITIALIZED, payload: true });
    };
    
    initializeAuth();
  }, []); // Run once on mount

  // Get user profile when login status changes
  useEffect(() => {
    if (authState.isLogin && authState.isInitialized && cookies[aToken]) {
      called && refetch ? refetch() : getCurrentUser();
    }
  }, [authState.isLogin, authState.isInitialized]);

  const logout = async () => {
    try {
      const result = await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      // Always clean up regardless of server response
      signOutCleanUp();
    }
  }

  const signOutCleanUp = () => {
    const isProd = process.env.NODE_ENV === "production";
    
    // Clear authentication state first to prevent further queries
    authDispatch({ type: SET_LOGIN_STATUS, payload: false });
    authDispatch({ type: CLEAR_CURRENT_USER });
    
    // Clear configuration cache
    sessionStorage.removeItem('app_configurations');
    
    // Stop any ongoing queries
    client.stop();
    authClient.stop();
    
    // Remove cookies
    removeCookie(aToken, { path: "/", domain: isProd ? window.location.hostname : "localhost" });
    removeCookie(rToken, { path: "/", domain: isProd ? window.location.hostname : "localhost" });
    
    // Reset Apollo stores
    client.resetStore();
    authClient.resetStore();
    
    navigate('/login', { replace: true });
  }

  const _getConfigurations = async () => {
    try {
      const { data } = await client.query({
        query: GET_CONFIGURATION_LIST,
        variables: {
          filter: {
            search: undefined,
            inputType: undefined,
          }
        },
        errorPolicy: 'all',
        fetchPolicy: 'network-only'
      });

      // Save configurations to session storage when loaded
      if (data?.getConfigurationList?.success && data.getConfigurationList.configurations) {
        sessionStorage.setItem('app_configurations', JSON.stringify(data.getConfigurationList.configurations));
      }
    } catch (error) {
      console.error('Error getting configurations:', error);
    }
  }

  const handleLoginSuccess = () => {
    _getConfigurations();
    navigate('/dashboard', { replace: true });
  };

  // Check for refresh token on mount
  useEffect(() => {
    if (!cookiesUni.get(rToken) && authState.isLogin) {
      signOutCleanUp();
    }
  }, [cookiesUni.get(rToken), authState.isLogin]);

  // Set loading spinner
  useEffect(() => {
    setLoading(loading);
  }, [loading]);

  // Set user profile when data changes
  useEffect(() => {
    if (data && !error) {
      authDispatch({ type: SET_CURRENT_USER, payload: data });
    } else if (error) {
      console.error('Error fetching current user:', error);
      // If token is invalid, logout
      if (error.message.includes('INVALID_TOKEN') || error.message.includes('401')) {
        signOutCleanUp();
      }
    }
  }, [data, error]);

  // Handle notifications
  useEffect(() => {
    if (layoutState.showNotification && layoutState.showNotification.message) {
      openNotificationWithIcon();
    }
  }, [layoutState.showNotification]);

  const protectedRoutesAccessCheck = (route: RouteInfo) => {
    if (!authState.isLogin) {
      return <Navigate to="/login" replace />;
    }
    
    return (
      <Suspense fallback={<Spin />}>
        <route.component routes={route.routes} />
      </Suspense>
    );
  }

  const openNotificationWithIcon = () => {
    notification[layoutState?.showNotification?.type]({
      message: layoutState?.showNotification.message,
      description: layoutState?.showNotification?.description,
    });
    layoutDispatch({ type: CLEAR_NOTIFICATION })
  };

  // Show loading until auth is initialized
  if (!authState.isInitialized) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" tip="Initializing..." />
      </div>
    );
  }

  // If user is not logged in, show login page
  if (!authState.isLogin) {
    return (
      <IntlProvider locale={layoutState.locale} messages={messages[layoutState.locale]}>
        <Spin tip="Loading..." spinning={layoutState.showLoading}>
          <Routes>
            <Route 
              path="/login" 
              element={
                <Login 
                  onLoginSuccess={handleLoginSuccess}
                />
              } 
            />
            <Route 
              path="/signup" 
              element={<SignUp />}
            />
            {/* Redirect all other routes to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Spin>
      </IntlProvider>
    );
  }

  // If user is logged in, show main app layout
  return (
    <IntlProvider locale={layoutState.locale} messages={messages[layoutState.locale]}>
      <Spin tip="Loading..." spinning={layoutState.showLoading}>
        <Layout className="app-container">
          <SideMenu />
          <Layout className="site-layout">
            <Header logout={logout} />
            <Content className="site-layout-background">
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/login" element={<Navigate to="/" replace />} />
                
                {routes.map((route: RouteInfo, i) => {
                  return (
                    <Route 
                      key={i} 
                      path={route.path} 
                      element={protectedRoutesAccessCheck(route)} 
                    />
                  );
                })}
                
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Content>
            <Footer />
          </Layout>
        </Layout>
      </Spin>
    </IntlProvider>
  );
}

export default App;